<script setup>
import { computed, useAttrs } from 'vue';

import { useTheme } from '../composables/useTheme';
import {
  HrExpansionItem, HrIcon, HrItemLabel, HrItemSection,
} from '../primitives';

/**
 * Expandable list row that reveals inline content on toggle.
 *
 * @status recommended
 * @group lists-tables
 * @useWhen Collapsible section within a list where sub-content is revealed in-place on toggle
 * @useWhen A collapsible row needs the same treatment as its non-expanding siblings — a segmented
 *   card, a trailing action, or a controlled open state for one-open-at-a-time lists
 * @related HrListItem
 * @related HrListGroup
 * @within hr-list
 * @a11y Inherits HrExpansionItem keyboard support — toggle with Enter or Space.
 */
defineOptions({ name: 'HrListExpansion' });

const props = defineProps({
  /**
   * Header text for the expansion row.
   * @example Section title
   */
  label: {
    type: String,
    default: null,
  },
  /** Supporting text shown below the header label. */
  caption: {
    type: String,
    default: null,
  },
  /** Material icon name for the leading section. */
  icon: {
    type: String,
    default: null,
  },
  /** Expands the item on initial render. */
  defaultOpened: {
    type: Boolean,
    default: false,
  },
  /**
   * Clamps the header label to this many lines with an ellipsis.
   * Useful in compact contexts like notification popups.
   */
  labelLines: {
    type: Number,
    default: null,
  },
  /**
   * Controlled open state; null lets the row manage its own. Declared here rather than left to
   * attribute fallthrough so a list that keeps exactly one row open has a supported way to do it.
   */
  modelValue: {
    type: Boolean,
    default: null,
  },
  /**
   * Renders the row in the M3 "segmented" style — a filled surface-container rounded card, so each
   * row reads as a discrete card at rest. The treatment lives on HrExpansionItem, the way HrListItem
   * takes it from HrItem, so a list may mix expanding and non-expanding rows without them looking
   * like different kinds of thing.
   */
  segmented: {
    type: Boolean,
    default: false,
  },
  /**
   * Removes the segmented card's horizontal inset so it sits flush with its container.
   *
   * `segmented` insets each card by 8px, which is right for a list that owns its surface. It is
   * wrong wherever the list sits inside another layout — a form grid, say — where every neighboring
   * control is flush to the column and the inset reads as misalignment. Has no effect without
   * `segmented`.
   */
  flush: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits({
  /** Emitted when the open state changes; payload is the new boolean value. */
  'update:modelValue': null,
});

const attrs = useAttrs();
const { hasTheme } = useTheme();

const labelStyle = computed(() => (props.labelLines !== null ? { '--hr-label-lines': props.labelLines } : undefined));
const labelClamped = computed(() => props.labelLines !== null);

const expansionProps = computed(() => ({
  ...(props.label !== null && { label: props.label }),
  ...(props.caption !== null && { caption: props.caption }),
  ...(props.icon !== null && { icon: props.icon }),
  ...(props.modelValue !== null && { modelValue: props.modelValue }),
  defaultOpened: props.defaultOpened,
  segmented: props.segmented,
  flush: props.flush,
  ...attrs,
}));
</script>

<template>
  <hr-expansion-item
    :class="[
      hasTheme ? 'hr-list-expansion' : 'hr-list-expansion-legacy',
      { 'hr-list-expansion--label-clamped': labelClamped },
    ]"
    :style="labelStyle"
    v-bind="expansionProps"
    @update:model-value="emit('update:modelValue', $event)"
  >
    <!--
      The header is only rebuilt when a trailing action needs somewhere to go. Quasar's own header
      renders label, caption and icon perfectly well, and replacing it unconditionally would mean
      this component owning that layout forever.
    -->
    <template
      v-if="$slots.trailing"
      #header
    >
      <hr-item-section
        v-if="icon !== null"
        avatar
      >
        <hr-icon :name="icon" />
      </hr-item-section>
      <hr-item-section>
        <hr-item-label>{{ label }}</hr-item-label>
        <hr-item-label
          v-if="caption !== null"
          caption
        >
          {{ caption }}
        </hr-item-label>
      </hr-item-section>
      <!--
        `@click.stop` so a menu button in here does not toggle the row it sits on — the whole header
        is the toggle target, which is what makes a trailing action unusable without this.
      -->
      <hr-item-section
        side
        @click.stop
      >
        <!-- @slot Trailing header content, e.g. an overflow menu or status text. -->
        <slot name="trailing" />
      </hr-item-section>
    </template>

    <!-- @slot Content revealed when the item is expanded. -->
    <slot />
  </hr-expansion-item>
</template>

<style lang="scss" scoped>

/* ── Header row base color ───────────────────────────────────────── */
.hr-list-expansion :deep(.q-item) {
  color: var(--hr-on-container, var(--app-on-surface));
}

/* ── Main label — body-large (M3 list item headline) ────────────── */
.hr-list-expansion :deep(.q-item__label) {
  color: var(--hr-on-container, var(--app-on-surface));
  font-size:      var(--app-type-body-large-size);
  line-height:    var(--app-type-body-large-line-height);
  font-weight:    var(--app-type-body-large-weight);
  letter-spacing: var(--app-type-body-large-tracking);
}

/* ── Label line-clamping — only when labelLines prop is set ─────── */
.hr-list-expansion--label-clamped :deep(.q-item__label) {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-clamp: var(--hr-label-lines);
  -webkit-line-clamp: var(--hr-label-lines);
}

/* ── Caption — body-medium (M3 supporting text) ─────────────────── */
/* Higher specificity than the rule above, so this wins for captions. */
.hr-list-expansion :deep(.q-item__label--caption) {
  color: var(--hr-on-container, var(--app-on-surface-variant));
  font-size:      var(--app-type-body-medium-size);
  line-height:    var(--app-type-body-medium-line-height);
  font-weight:    var(--app-type-body-medium-weight);
  letter-spacing: var(--app-type-body-medium-tracking);
}

/* ── Leading icon section — 40px (M3 icon list item width) ──────── */
.hr-list-expansion :deep(.q-item__section--avatar) {
  color: var(--hr-on-container, var(--app-on-surface-variant));
}

.hr-list-expansion-legacy :deep(.q-item__section--avatar),
.hr-list-expansion :deep(.q-item__section--avatar) {
  min-width: 40px;
  max-width: 40px;
}

/* ── Expand toggle icon ──────────────────────────────────────────── */
.hr-list-expansion :deep(.q-expansion-item__toggle-icon) {
  color: var(--hr-on-container, var(--app-on-surface-variant));
}

</style>
